import { Dialog } from '@madpaws/design-system';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

// import { MobileVerificationForm } from './MobileVerificationForm';
import { FORM_IDS } from '~/common/constants/app';
import { ACCESS_TOKEN_COOKIE_NAME, MD_AUTH_LOCAL_STORAGE_KEY } from '~/common/constants/auth';
import { useAuthenticationContext } from '~/components/AuthenticationProvider/useAuthenticationContext';
import { TermsConfirmationForm } from '~/components/GoogleOAuthProvider/SignUpDialog/TermsConfirmationForm';
import { DOM_CONTAINER_ID } from '~/components/constants';
import { AuthApi } from '~/services/auth/authApi';
import { RestApi } from '~/services/restApi';
import { setLocalStorage } from '~/utils/localStorage';
import { sentryException } from '~/utils/sentryHelpers';

import { getSocialLoginArgs } from '../helpers/getSocialLoginArgs';

import type { SignUpMethod, SocialSignUpProps } from '../AuthDialog/AuthDialog';
import type { ReactElement } from 'react';
import type { LoginResponse } from '~/api/graphql/googleSignIn/typeDefs';
import type { SocialLoginArgs } from '~/services/auth/types';

export type Props = {
  onDialogClose: () => void;
  onSignUpSuccess: (
    data: LoginResponse,
    promoQueryParamCode?: string,
    loginMethod?: SignUpMethod
  ) => void;
  signUpData: (LoginResponse & { authMethod: SignUpMethod }) | null;
  termsFormError?: string | null;
  // onSuccessLogin: () => void;
};

enum GoogleAuthFormStep {
  Terms = 'terms',
  Mobile = 'mobile',
}

// Exported for use in input validation
export const SocialSignUpDialog = ({
  onDialogClose,
  onSignUpSuccess,
  signUpData,
  termsFormError,
}: Props): ReactElement => {
  const [step, setStep] = useState<GoogleAuthFormStep>(GoogleAuthFormStep.Terms);
  const [isTermsFormSubmitting, setIsTermsFormSubmitting] = useState<boolean>(false);

  const router = useRouter();
  const {
    route: routerRoute,
    query: { refSource, referralCode },
  } = router;
  // const [isNewUser, setIsNewUser] = useState<boolean>(false);

  const { isSocialSignUpDialogOpen, promo } = useAuthenticationContext();

  useEffect(() => {
    if (signUpData) {
      const { isUserPhoneNumberVerified, userNeedsToAcceptTerms } = signUpData;
      // if user needs to accept terms, show TermsConfirmationForm
      // regardless the value of isUserPhoneNumberVerified
      if (userNeedsToAcceptTerms) {
        setStep(GoogleAuthFormStep.Terms);
        // setIsNewUser(true);

        return;
      }
      // if user don't need to accept terms and user has unverified phone number
      // show MobileVerificationForm
      if (isUserPhoneNumberVerified === false) {
        setStep(GoogleAuthFormStep.Mobile);
      }
    }
  }, [signUpData]);

  const onCancel = (): void => {
    onDialogClose();
  };

  const sendSentryException = (error: unknown, socialLoginArgs: SocialLoginArgs): void => {
    sentryException(
      error,
      `signInWithSocial API failed, socialLoginArgs: ${JSON.stringify(socialLoginArgs, null, 2)}`
    );
  };

  const onSocialSignUpFormSubmit = async (data: SocialSignUpProps): Promise<void> => {
    setIsTermsFormSubmitting(true);

    const extraArgs = {
      one_tap: routerRoute,
      refSource: (refSource as string) ?? null,
      referralCode: (referralCode as string) ?? null,
    };

    const socialLoginArgs: SocialLoginArgs = (await getSocialLoginArgs(
      data.socialToken,
      signUpData?.authMethod as SignUpMethod,
      data,
      extraArgs
    )) as SocialLoginArgs;

    try {
      const res = await AuthApi.loginWithSocials(
        socialLoginArgs,
        signUpData?.authMethod as SignUpMethod
      );

      const auth = res as LoginResponse;
      Cookies.set(ACCESS_TOKEN_COOKIE_NAME, JSON.stringify({ tokens: auth }) ?? '');
      setLocalStorage(MD_AUTH_LOCAL_STORAGE_KEY, { ...auth });
      RestApi.setAuthorizationHeader({ authorization: `Bearer ${auth.access_token}` });
      setIsTermsFormSubmitting(false);

      onSignUpSuccess(res as LoginResponse, promo, signUpData?.authMethod as SignUpMethod);
    } catch (error) {
      sendSentryException(error, socialLoginArgs);
    }
  };

  return (
    <Dialog
      domContainerId={DOM_CONTAINER_ID}
      formId={FORM_IDS.SOCIAL_SIGN_UP_FORM}
      heading="Welcome to Mad Paws"
      isOpen={isSocialSignUpDialogOpen}
      onRequestClose={onDialogClose}
    >
      {step === GoogleAuthFormStep.Terms && (
        <TermsConfirmationForm
          data={signUpData}
          formError={termsFormError ?? null}
          isSubmitting={isTermsFormSubmitting}
          onCancel={onCancel}
          onSubmit={onSocialSignUpFormSubmit}
        />
      )}
      {/* {step === GoogleAuthFormStep.Mobile && (
        <MobileVerificationForm
        data={data}
        isNewUser={isNewUser}
        onSuccessLogin={onSuccessLogin} />
      )} */}
    </Dialog>
  );
};
